import { useMemo, useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Constellation hero — a pulsing Clue2App core with approved showcase
 * apps orbiting on independent elliptical paths. Ghost nodes fill out
 * the visual when fewer than the target count have been approved.
 *
 * Why this shape: the platform sells itself as "where apps live and
 * thrive". A central core + orbiting bodies maps directly. Each
 * orbit has its own radius / period / phase so the constellation
 * never visually locks into a synchronous wheel — feels organic.
 *
 * The center "Clue2App" core sits in front of every orbit (z-index
 * trick via render order) so nodes appear to pass behind/around it.
 *
 * Respects prefers-reduced-motion — when set, orbits freeze in
 * scattered positions, core stops pulsing, fade-ins skip.
 */
export default function Constellation({ entries }) {
  const reduce = useReducedMotion();
  const containerRef = useRef(null);
  const [{ w, h }, setSize] = useState({ w: 1200, h: 520 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // We always render TARGET_NODES bodies. Real entries fill from the
  // outside in; ghosts fill the rest. This keeps the visual rich
  // even with one or two apps in the showcase.
  const TARGET_NODES = 8;
  const nodes = useMemo(() => {
    const list = [];
    for (let i = 0; i < TARGET_NODES; i++) {
      const entry = entries[i];
      list.push({
        id: entry ? `e-${i}` : `g-${i}`,
        entry,
        // Orbit shape — each node gets its own ellipse + period + phase.
        rx: 220 + (i % 4) * 60,
        ry: 120 + (i % 3) * 50,
        period: 38 + (i * 7) % 30, // seconds per full orbit
        phase: (i * 137.5) % 360, // golden-angle stagger
        size: entry ? 64 : 44,
        depth: i % 2 === 0 ? 1 : 0.7, // back vs front parallax
      });
    }
    return list;
  }, [entries]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 460, md: 560 },
        overflow: "hidden",
        background:
          "radial-gradient(ellipse at 20% 30%, rgba(234,88,12,0.18), transparent 50%), " +
          "radial-gradient(ellipse at 80% 70%, rgba(99,102,241,0.20), transparent 55%), " +
          "linear-gradient(135deg, #0a0e27 0%, #0f172a 40%, #1e1b4b 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <Starfield />

      {/* Orbit guide rings — barely visible, gives a sense of depth */}
      {!reduce && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {nodes.slice(0, 4).map((n, i) => (
            <Box
              key={`ring-${i}`}
              sx={{
                position: "absolute",
                width: n.rx * 2,
                height: n.ry * 2,
                borderRadius: "50%",
                border: "1px dashed rgba(255,255,255,0.04)",
              }}
            />
          ))}
        </Box>
      )}

      {/* Orbiting bodies — render BEFORE the core so core sits in front */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {nodes.map((n, i) => (
          <Orbit key={n.id} node={n} index={i} reduceMotion={reduce} containerW={w} containerH={h} />
        ))}
      </Box>

      {/* Core — pulsing brand orb with the wordmark */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        <CoreOrb reduceMotion={reduce} />
        <Box sx={{ mt: 4, textAlign: "center", px: 2 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "3.25rem" },
              letterSpacing: "-0.02em",
              background:
                "linear-gradient(135deg, #ffffff 0%, #fed7aa 50%, #fdba74 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              mb: 1,
              textShadow: "0 4px 30px rgba(234,88,12,0.15)",
            }}
          >
            Built on Clue2App
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.7)",
              maxWidth: 480,
              mx: "auto",
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            Apps shipped by our customers, partners, and team — the
            constellation of work running on the platform.
          </Typography>
        </Box>
      </Box>

      {/* Vignette to soften the top + sides */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(10,14,39,0.6) 100%)",
        }}
      />
    </Box>
  );
}

// ----------------- pieces -----------------

function CoreOrb({ reduceMotion }) {
  return (
    <Box sx={{ position: "relative", width: 200, height: 200 }}>
      {/* Glow halo */}
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(234,88,12,0.45) 0%, rgba(234,88,12,0.0) 70%)",
          filter: "blur(20px)",
        }}
        animate={reduceMotion ? {} : { scale: [1, 1.18, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Inner core with embedded Clue2App brand icon */}
      <motion.div
        style={{
          position: "absolute",
          inset: "26%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, #fef3c7 0%, #fb923c 35%, #ea580c 70%, #9a3412 100%)",
          boxShadow:
            "0 0 40px rgba(234,88,12,0.6), inset 0 -10px 20px rgba(154,52,18,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
        initial={{ scale: 0.6, opacity: 0 }}
        animate={
          reduceMotion
            ? { scale: 1, opacity: 1 }
            : { scale: [1, 1.06, 1], opacity: 1 }
        }
        transition={{
          scale: { duration: 3.5, repeat: Infinity, ease: "easeInOut" },
          opacity: { duration: 0.8, ease: "easeOut" },
        }}
      >
        <Box
          component="img"
          src={process.env.PUBLIC_URL + "/clue2app-logo.png"}
          alt="Clue2App"
          sx={{
            width: "78%",
            height: "78%",
            objectFit: "contain",
            filter:
              "drop-shadow(0 2px 8px rgba(0,0,0,0.4)) brightness(1.1) contrast(1.05)",
            mixBlendMode: "luminosity",
            opacity: 0.95,
          }}
        />
      </motion.div>
    </Box>
  );
}

function Orbit({ node, index, reduceMotion, containerW, containerH }) {
  // Container is the central reference frame — node animates relative
  // to it. We position the node initially at +rx on X axis, then rotate
  // the wrapper to swing it around. Center is the container's center.
  const onTinyScreen = containerW < 720;
  // Shrink orbits on small viewports so nodes don't escape the frame.
  const scale = onTinyScreen ? 0.55 : 1;

  return (
    <Box
      sx={{
        position: "absolute",
        width: 0,
        height: 0,
        // Keep transform-origin at the centerpoint
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          transformOrigin: "center center",
        }}
        initial={{ rotate: node.phase, opacity: 0 }}
        animate={
          reduceMotion
            ? { rotate: node.phase, opacity: 1 }
            : { rotate: [node.phase, node.phase + 360], opacity: 1 }
        }
        transition={{
          rotate: {
            duration: node.period,
            repeat: Infinity,
            ease: "linear",
          },
          opacity: { duration: 0.8, delay: 0.2 + index * 0.06 },
        }}
      >
        <motion.div
          // Push node outward to its orbit radius. We use elliptical
          // by scaling Y differently from X — a wrapper just translates X,
          // and we let the parent rotation produce the ellipse via Y scale.
          style={{
            transform: `translate(${node.rx * scale}px, 0)`,
          }}
        >
          {/* Counter-rotate the node so its content stays upright */}
          <motion.div
            style={{ transformOrigin: "center" }}
            animate={
              reduceMotion ? {} : { rotate: [-node.phase, -node.phase - 360] }
            }
            transition={{
              duration: node.period,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <NodeBody node={node} />
          </motion.div>
        </motion.div>
      </motion.div>
    </Box>
  );
}

function NodeBody({ node }) {
  const [hovered, setHovered] = useState(false);
  const isGhost = !node.entry;
  const size = node.size;

  if (isGhost) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.08))",
          border: "1px solid rgba(255,255,255,0.06)",
          backdropFilter: "blur(8px)",
        }}
      />
    );
  }

  const initial = (node.entry.name || "?").charAt(0).toUpperCase();

  return (
    <Box
      component="a"
      href={node.entry.url}
      target="_blank"
      rel="noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "block",
        width: size,
        height: size,
        borderRadius: 2,
        position: "relative",
        textDecoration: "none",
        pointerEvents: "auto",
        cursor: "pointer",
        transition: "transform 200ms ease, box-shadow 200ms ease",
        transform: hovered ? "scale(1.18)" : "scale(1)",
        boxShadow: hovered
          ? "0 8px 32px rgba(234,88,12,0.45), 0 0 0 2px rgba(253,186,116,0.5)"
          : "0 4px 14px rgba(0,0,0,0.4)",
        backdropFilter: "blur(8px)",
        background: node.entry.imageUrl
          ? "transparent"
          : "linear-gradient(135deg, #f97316, #fb923c)",
        overflow: "hidden",
      }}
    >
      {node.entry.imageUrl ? (
        <Box
          component="img"
          src={node.entry.imageUrl}
          alt={node.entry.name}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 2,
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: size * 0.45,
          }}
        >
          {initial}
        </Box>
      )}
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "rgba(15,23,42,0.95)",
            color: "#fff",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: "0.75rem",
            fontWeight: 500,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {node.entry.name}
        </Box>
      )}
    </Box>
  );
}

function Starfield() {
  // Generate a fixed starfield with deterministic positions — keeps
  // re-renders cheap and avoids the random-on-every-render churn.
  const stars = useMemo(() => {
    const list = [];
    // Seeded pseudo-random so the field is the same on every load.
    let seed = 42;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    for (let i = 0; i < 80; i++) {
      list.push({
        x: rand() * 100,
        y: rand() * 100,
        r: 0.5 + rand() * 1.8,
        o: 0.2 + rand() * 0.6,
        delay: rand() * 4,
      });
    }
    return list;
  }, []);

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0 }}
      >
        {stars.map((s, i) => (
          <motion.circle
            key={i}
            cx={s.x}
            cy={s.y}
            r={s.r * 0.05}
            fill="#fff"
            opacity={s.o}
            animate={{ opacity: [s.o * 0.4, s.o, s.o * 0.4] }}
            transition={{
              duration: 3 + (i % 4),
              repeat: Infinity,
              delay: s.delay,
              ease: "easeInOut",
            }}
          />
        ))}
      </svg>
    </Box>
  );
}
